/**
 * Holds on to Chrome's install prompt so we can offer it when it suits the
 * member rather than when the browser happens to fire.
 *
 * `beforeinstallprompt` fires once, early, and is lost if nothing calls
 * `preventDefault()` on it — which is why this is initialised from `index.js`
 * rather than from a screen. By the time any screen mounts, the event has
 * usually already gone.
 *
 * **iOS never fires this event at all.** Safari has no install API, so on an
 * iPhone the only option is telling the member to tap Share and then Add to
 * Home Screen by hand. That asymmetry is the reason the install guide needs two
 * completely different sets of instructions.
 */

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn(deferredPrompt !== null));
}

export function initInstallPrompt() {
  if (typeof window === "undefined") return;

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export function canPromptInstall() {
  return deferredPrompt !== null;
}

/** Returns true if they accepted. The prompt is single-use either way. */
export async function promptInstall() {
  if (!deferredPrompt) return false;

  const event = deferredPrompt;
  deferredPrompt = null;
  notify();

  event.prompt();
  const choice = await event.userChoice;
  return choice?.outcome === "accepted";
}

/** Subscribe to availability changes. Returns an unsubscribe function. */
export function onInstallPromptChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
